import React, { useEffect, useState } from 'react';

const API_HOST =
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const buildProxyUrl = (fileId, sizeHint = 'w1600-h1600') => {
  if (!fileId) return '';
  return `/api/image-proxy?id=${encodeURIComponent(fileId)}&sz=${encodeURIComponent(sizeHint)}`;
};

const extractDriveId = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const rawTrim = raw.trim();
  if (rawTrim.startsWith('/api/image-proxy')) return null;
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
    /uc\?export=view&id=([a-zA-Z0-9_-]+)/,
    /thumbnail\?id=([a-zA-Z0-9_-]+)/,
    /lh3\.googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/,
    /^([a-zA-Z0-9_-]{25,})$/
  ];
  for (const p of patterns) {
    const m = rawTrim.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
};

const SmartImage = ({ src, alt, style, className, fallbackIcon = 'fas fa-image' }) => {
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCurrentUrlIndex(0);
    setHasError(false);
  }, [src]);

  const normalizeImageUrl = (raw) => {
    if (!raw) return '';
    let url = String(raw).trim();

    // 🔑 Si c'est une URL Google Drive → proxy backend pour éviter ORB
    const driveId = extractDriveId(url);
    if (driveId) {
      return buildProxyUrl(driveId, 'w1600-h1600');
    }

    // Already absolute http(s)
    if (/^https?:\/\//i.test(url)) return url;

    // Normalize backslashes
    url = url.replace(/\\/g, '/');
    // Ensure leading slash for uploads
    if (!url.startsWith('/')) url = `/${url}`;
    // Prepend backend host for uploaded assets
    if (url.startsWith('/uploads/')) {
      return `${API_HOST}${url}`;
    }
    return url;
  };

  const getProxyUrlFormats = (fileId) => {
    return [
      buildProxyUrl(fileId, 'w2000-h2000'),
      buildProxyUrl(fileId, 'w1600-h1600'),
      buildProxyUrl(fileId, 'w1200'),
      buildProxyUrl(fileId, 'w1000-h1000'),
      buildProxyUrl(fileId, 'w600-h600'),
    ];
  };

  const handleError = () => {
    if (!src) {
      setHasError(true);
      return;
    }

    const normalizedSrc = normalizeImageUrl(src);
    const driveId = extractDriveId(src);

    if (driveId) {
      const urlFormats = getProxyUrlFormats(driveId);
      if (currentUrlIndex < urlFormats.length - 1) {
        setCurrentUrlIndex(currentUrlIndex + 1);
      } else {
        setHasError(true);
      }
    } else {
      setHasError(true);
    }
  };

  const handleLoad = () => {
    // Image chargée avec succès
  };

  if (!src || hasError) {
    return (
      <div
        className={`smart-image-fallback ${className || ''}`}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          color: '#999',
          fontSize: '24px'
        }}
      >
        <i className={fallbackIcon}></i>
      </div>
    );
  }

  const normalizedSrc = normalizeImageUrl(src);
  const driveId = extractDriveId(src);

  let currentSrc = normalizedSrc;
  if (driveId) {
    const urlFormats = getProxyUrlFormats(driveId);
    currentSrc = urlFormats[currentUrlIndex] || urlFormats[0];
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      style={style}
      className={className}
      referrerPolicy="no-referrer"
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

export default SmartImage;
